'use client';

import { useState } from 'react';
import { Search, CheckCircle, AlertCircle, Info, Loader2 } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { plagiarismCheckerContent } from '../../data/plagiarism-checker-content';

export default function PlagiarismChecker() {
    const [text, setText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [result, setResult] = useState<{
        score: number;
        matches: Array<{
            url: string;
            similarity: number;
            title: string;
        }>;
    } | null>(null);
    const [error, setError] = useState('');

    const checkPlagiarism = async () => {
        if (!text.trim()) {
            setError('Please enter some text to check');
            return;
        }

        if (text.trim().split(' ').length < 10) {
            setError('Please enter at least 10 words for an accurate check');
            return;
        }

        setIsLoading(true);
        setError('');
        setResult(null);

        try {
            const response = await fetch('/api/plagiarism-check', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ text }),
            });

            if (!response.ok) {
                throw new Error('Failed to check for plagiarism');
            }

            const data = await response.json();
            setResult(data);
        } catch (err) {
            console.error('Plagiarism check failed:', err);
            setError('Failed to check for plagiarism. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="container mx-auto px-6 sm:px-8 py-12">
            <div className="max-w-6xl mx-auto">
                <ToolPageIntro title="Plagiarism Checker - Free Online Content Originality Tool">
                Check your content for plagiarism with our free online plagiarism detector. Ensure originality and academic integrity with comprehensive similarity analysis.
            </ToolPageIntro>

                <div className="bg-white rounded-lg shadow-md p-6 mb-8">
                    <h2 className="text-xl font-semibold mb-4">Enter your text</h2>
                    <p className="text-gray-600 mb-4">
                        Paste the text you want to check for plagiarism (minimum 10 words)
                    </p>

                    <div className="space-y-4">
                        <textarea
                            value={text}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                            placeholder="Paste your text here..."
                            className="w-full p-3 border border-gray-300 rounded-md min-h-[200px] focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            disabled={isLoading}
                        />
                        <div className="flex justify-end">
                            <button
                                onClick={checkPlagiarism}
                                disabled={isLoading || !text.trim()}
                                className={`px-4 py-2 rounded-md text-white font-medium flex items-center ${isLoading || !text.trim() ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'
                                    }`}
                            >
                                {isLoading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Checking...
                                    </>
                                ) : (
                                    <>
                                        <Search className="mr-2 h-4 w-4" />
                                        Check for Plagiarism
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" />
                            <span>{error}</span>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <h2 className="text-xl font-semibold mb-4">Results</h2>
                        <p className="mb-4">
                            Similarity Score: <span className={`font-semibold ${result.score > 50 ? 'text-red-600' : 'text-green-600'}`}>
                                {result.score}%
                            </span>
                        </p>

                        {result.score > 50 ? (
                            <div className="space-y-4">
                                <div className="flex items-center text-red-600">
                                    <AlertCircle className="h-5 w-5 mr-2" />
                                    <span>Warning: High similarity detected. Consider rewriting the content to be more original.</span>
                                </div>

                                {result.matches.length > 0 && (
                                    <div>
                                        <h3 className="font-medium mb-3 text-lg">Matching sources found:</h3>
                                        <div className="space-y-3">
                                            {result.matches.map((match, index) => (
                                                <div key={index} className="p-4 border rounded-md hover:bg-gray-50">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <a
                                                                href={match.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-blue-600 hover:underline break-all"
                                                            >
                                                                {match.title || match.url}
                                                            </a>
                                                            <div className="text-sm text-gray-500 mt-1">
                                                                {match.similarity}% similar
                                                            </div>
                                                        </div>
                                                        <div className="text-sm font-medium text-gray-700">
                                                            {match.similarity}%
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center text-green-600">
                                <CheckCircle className="h-5 w-5 mr-2" />
                                <span>Your content appears to be original. No significant matches found.</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Informational Content */}
                <div className="mt-12">
                    <InformationalContent
                        title={plagiarismCheckerContent.title}
                        description={plagiarismCheckerContent.description}
                        content={plagiarismCheckerContent.content}
                        faqs={plagiarismCheckerContent.faqs}
                        relatedTools={plagiarismCheckerContent.relatedTools}
                    />
                </div>
            </div>
        </div>
    );
}
